import type { unstable_RemoteThreadListAdapter as RemoteThreadListAdapterType } from '@assistant-ui/react';
import type { LanguageModel } from 'ai';
import { generateText } from 'ai';
import { createAssistantStream } from 'assistant-stream';

import { createThreadListStore } from './localStorage.ts';

/** Creates a thread list adapter backed by localStorage for thread persistence. */
export function createThreadListAdapter(model: LanguageModel): RemoteThreadListAdapterType {
  const store = createThreadListStore(window.localStorage);

  return {
    async list() {
      return store.list();
    },

    async initialize(localId) {
      return store.initialize(localId);
    },

    async rename(remoteId, newTitle) {
      store.rename(remoteId, newTitle);
    },

    async archive(remoteId) {
      store.archive(remoteId);
    },

    async unarchive(remoteId) {
      store.unarchive(remoteId);
    },

    async delete(remoteId) {
      store.delete(remoteId);
    },

    async fetch(remoteId) {
      return store.fetch(remoteId);
    },

    async generateTitle(remoteId, messages) {
      return createAssistantStream(async (controller) => {
        const { text: title } = await generateText({
          model,
          system:
            'Generate a short (3-6 word) title for this conversation. Respond with ONLY the title, no quotes or punctuation.',
          messages: messages.map((m) => ({
            role: m.role as 'user' | 'assistant',
            content: m.content
              .filter((c): c is { type: 'text'; text: string } => c.type === 'text')
              .map((c) => c.text)
              .join('\n'),
          })),
        });

        store.rename(remoteId, title);
        controller.appendText(title);
      });
    },
  };
}
